'use client'

import { SizeSpacing3 } from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'

import { type ForwardedRef, forwardRef } from 'react'
import { onlyText } from 'react-children-utilities'

import type { ChildrenProps, TextAlignment } from '../../types'

import { slugify } from '../../helpers'
import { handlePaste } from '../../helpers/clipboard'
import { asPx } from '../../styles/helpers'

export const TYPE = 'heading'

export const VARIANTS = [
  // 'heading-1',
  'heading-2',
  'heading-3',
  'heading-4',
] as const
export type HeadingVariant = (typeof VARIANTS)[number]
export const DEFAULT_VARIANT = 'heading-2'

export const DEFAULT_TEXT_ALIGNMENT: TextAlignment = 'left'

type Props = {
  empty?: boolean
  forHtml?: boolean
  id?: string
  level?: HeadingVariant
  placeholder?: string
  textAlignment?: TextAlignment
  onPaste?: (content: string) => void
} & Partial<ChildrenProps>
const Heading = forwardRef<HTMLElement, Props>(
  (
    {
      children,
      empty = true,
      forHtml = false,
      id,
      level = DEFAULT_VARIANT,
      onPaste,
      placeholder,
      textAlignment = DEFAULT_TEXT_ALIGNMENT,
    }: Props,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    if (forHtml) {
      return (
        <Typography
          id={id}
          ref={ref}
          inlineStyle
          style={{
            fontStyle: 'normal',
            marginBottom: asPx(SizeSpacing3, { withUnit: true }),
            textAlign:
              textAlignment === 'center' || textAlignment === 'right'
                ? textAlignment
                : undefined,
          }}
          variant={level}
        >
          {ref ? undefined : children}
        </Typography>
      )
    }

    let fontSizeClassName = ''
    switch (level) {
      case 'heading-2': {
        fontSizeClassName =
          '![font-size:var(--typography-heading-2-font-size-xxs)] @sm:![font-size:var(--typography-heading-2-font-size-md)]'
        break
      }

      case 'heading-3': {
        fontSizeClassName = ''
        break
      }

      case 'heading-4': {
        fontSizeClassName = ''
        break
      }

      default: {
        break
      }
    }

    let alignClassName = ''
    switch (textAlignment) {
      case 'center': {
        alignClassName = 'text-center'
        break
      }

      case 'right': {
        alignClassName = 'text-right'
        break
      }

      default: {
        break
      }
    }

    const slug = slugify(onlyText(children)).replaceAll('_', '-')

    return (
      <div className="w-full">
        <a id={slug} className="no-underline" href={`#${slug}`}>
          <Typography
            id={id}
            ref={ref}
            aria-placeholder={empty ? placeholder : undefined}
            className={cj(
              alignClassName,
              fontSizeClassName,
              children ? 'whitespace-pre-line' : '',
            )}
            data-level={level}
            data-type={TYPE}
            variant={level}
            onPaste={handlePaste(onPaste)}
          >
            {ref ? undefined : children}
          </Typography>
        </a>
      </div>
    )
  },
)
Heading.displayName = 'Heading'

export default Heading
